'use client';

import clsx from 'clsx';
import type React from 'react';
import {
  type ChangeEvent,
  type HTMLInputAutoCompleteAttribute,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import styles from './OutlinedTextField.module.scss';

export type OutlinedTextFieldProps = {
  className: string;
  invalid: boolean;
  placeholder: string;
  label: string;
  icon: React.JSX.Element;
  helpText: string | React.JSX.Element;
  disabled: boolean;
  required: boolean;
  autoComplete: HTMLInputAutoCompleteAttribute | undefined;
  minLength: number;
  maxLength: number;
  value: string | number;
  secondaryBackground: boolean;
  tertiaryBackground: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onChange?: (...event: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onKeyDown?: (...event: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onBlur?: (...event: any[]) => void;
  type?: 'text' | 'password' | 'email' | 'number';
  name?: string;
  dataTestId?: string;
};

const OutlinedTextField = forwardRef(
  (props: Partial<OutlinedTextFieldProps>, ref) => {
    const [inputValue, setInputValue] = useState(props.value);

    function updateInput(event: ChangeEvent<HTMLInputElement>) {
      setInputValue(event.target.value);
      props.onChange?.(event.target.value);
    }

    useEffect(() => {
      setInputValue(props.value);
    }, [props.value]);

    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div
            className={clsx(
              props.className,
              styles.textfield,
              props.invalid && styles.hasError,
            )}
          >
            <input
              className={clsx(
                styles.textFieldInput,
                props.secondaryBackground && styles.secondaryBackground,
                props.tertiaryBackground && styles.tertiaryBackground,
              )}
              placeholder={props.placeholder || ''}
              disabled={props.disabled}
              minLength={props.minLength}
              maxLength={props.maxLength}
              value={inputValue}
              onChange={updateInput}
              onKeyDown={props.onKeyDown}
              onBlur={props.onBlur}
              data-testid={props.dataTestId || props.label}
              id={props.label}
              type={props.type || 'text'}
              name={props.name || 'name'}
              required={props.required || false}
              autoComplete={props.autoComplete && props.autoComplete}
            />
            <label
              htmlFor={props.label}
              className={clsx(
                styles.textFieldLabel,
                props.disabled && styles.disabled,
                props.secondaryBackground && styles.secondaryBackground,
                props.tertiaryBackground && styles.tertiaryBackground,
              )}
            >
              {props.label}
            </label>
          </div>
          <span className={styles.icon}>{props.icon}</span>
        </div>
        <span className={styles.helpText}>{props.helpText}</span>
      </div>
    );
  },
);

export {OutlinedTextField};
